import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Badge, ResponsiveDataTable, Typography } from '@tq/ui-components';

import { EmptyDataCard, hiddenTableHeaderLabel } from 'components';
import { Consultation, Document } from 'types';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { DocumentsMobileCard } from './DocumentsMobileCard';
import { useDocumentFilters } from './hooks';
import { useDocumentMenuOptions } from '../../hooks';
import { renderDocumentMenuActionButton } from '../../util';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

export type DocumentsProps = {
  consultation?: Consultation;
  documents?: Document[];
};

export const Documents: React.FC<DocumentsProps> = ({ consultation, documents }) => {
  const { t } = useTranslation('case_details_documents');
  const options = useDocumentMenuOptions();

  const isMobile = useIsMobile();
  const formatLocaleDate = useFormatLocaleDate();
  const filters = useDocumentFilters(documents, true);
  const filterColumnLayout = isMobile ? '100%' : '33% 65%';

  const renderSensitiveLabel = () => (
    <Badge color={COLOURS.black} bgColor={COLOURS.blue[100]}>
      <strong>{t('sensitive', 'Sensitive')}</strong>
    </Badge>
  );

  return (
    <>
      {isMobile && (
        <Typography mx={4} mb={4}>
          <h2>{t('documents', 'Documents')}</h2>
        </Typography>
      )}
      {documents?.length ? (
        <ResponsiveDataTable
          Card={(row) => (
            <DocumentsMobileCard
              consultation={consultation}
              document={row}
              options={options}
            />
          )}
          filterColumnLayout={filterColumnLayout}
          filters={filters}
          initialState={{
            sortBy: [
              {
                id: 'datePublished',
                desc: true,
              },
            ],
          }}
          data={documents}
          columns={[
            {
              accessor: 'datePublished',
              Cell: (cellProps: { value: string }) => formatLocaleDate(cellProps.value),
              Header: () => t('date_published', 'Date published'),
              width: 180,
            },
            {
              accessor: 'documentType',
              Cell: (cellProps: { value: string }) => cellProps.value,
              Header: () => t('document_type', 'Document type'),
              width: 'auto',
            },
            {
              accessor: 'description',
              disableSortBy: true,
              Header: () => t('document_description', 'Document description'),
              width: 'auto',
            },
            {
              accessor: 'sensitive',
              Cell: (cellProps: { value: boolean }) =>
                cellProps.value && renderSensitiveLabel(),
              disableSortBy: true,
              Header: () => hiddenTableHeaderLabel(t('sensitive_label', 'is sensitive')),
              width: 20,
            },
            {
              accessor: 'actions',
              Cell: (cellProps: { row: { original: Document } }) =>
                renderDocumentMenuActionButton(consultation, options, cellProps.row.original),
              disableSortBy: true,
              Header: () => hiddenTableHeaderLabel(t('actions_label', 'actions')),
              width: 20,
            },
          ]}
        />
      ) : (
        <EmptyDataCard message={t('no_documents', 'No documents available')} />
      )}
    </>
  );
};
